import React, { useState, useEffect } from 'react'
import { httpClient } from "../../funcs"
import { notifyAboutError } from "../../methods/notifyUser"
import { formatDateTime } from "../../methods/formatDate"
import { ListLoader } from "../Contracts/ContractList/PageLoader"
import { ErrorPageView } from "../../components/ErrorPageView"
import PackageItem, { PackageItemData } from './PackageItem'
import { PurchasedUpgradeTag, UpgradeTagsRow } from './UpgradeTag'
import "pages/Styles/PackagesList.sass"

interface Package {
    _id: string
    user_id: string
    tariff_id: string
    size: number
    remaining_documents: number
    purchased_at: string
    expires_at?: string
    status: string
    active_upgrades: string[]
    tariff_upgrades?: string[]
    transaction_id?: string
    payment_method?: string
    is_current_spend?: boolean
    tariff_info?: {
        name: string
        prepaid_documents_count: number
        period?: string
    }
}

interface PackagesListProps {
    $store: any
    $commitToStore: (data: any) => void
}

const PackagesList: React.FC<PackagesListProps> = ({ $store, $commitToStore }) => {
    const [packages, setPackages] = useState<Package[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<any>(null)

    useEffect(() => {
        loadPackages()
    }, [])

    async function loadPackages() {
        try {
            setLoading(true)
            setError(null)
            const { data } = await httpClient.get("/user-packages")
            setPackages(data.packages || [])
        } catch (error) {
            console.error("Ошибка при загрузке списка пакетов:", error)
            setError(error)
            notifyAboutError({ $commitToStore }, error)
        } finally {
            setLoading(false)
        }
    }

    function formatDate(dateString: string): string {
        return formatDateTime(dateString)
    }

    function getPaymentMethodText(packageItem: Package): string | null {
        // Если есть payment_method_id - это точно карта
        if (packageItem.payment_method) {
            return packageItem.payment_method === 'card' ? 'Карта' : 'Счет'
        }
        
        // По умолчанию null
        return null
    }

    function getValidUntilDate(expiresAt?: string): string | null {
        if (!expiresAt) {
            return null
        }

        const expiryDate = new Date(expiresAt)

        // Форматируем дату в российском формате
        return expiryDate.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    function mapPackageToMainItem(packageItem: Package): PackageItemData {
        const tariffName = packageItem.tariff_info?.name || 'Пакет документов'
        const title = 'Пакет: ' + tariffName
        let subtitle = ''

        if (packageItem.size !== -1) {
            subtitle = `${packageItem.size} документов`
        }

        const validUntil = getValidUntilDate(packageItem.expires_at)
        if (validUntil) {
            subtitle += ` • действует до ${validUntil}`
        }

        if (packageItem.remaining_documents !== undefined && packageItem.size !== -1) {
            subtitle += ` • ${packageItem.remaining_documents} осталось`
        }

        return {
            title,
            subtitle,
            isMain: true,
            badge: packageItem.is_current_spend ? 'Списывается' : undefined
        }
    }

    function renderPackage(packageItem: Package) {
        const mainItem = mapPackageToMainItem(packageItem)
        const upgrades = packageItem.active_upgrades?.length
            ? packageItem.active_upgrades
            : (packageItem.tariff_upgrades || [])

        return (
            <div key={packageItem._id} className="package-card">
                <div className="package-main-content">
                    {/* Левая часть - дата покупки и способ оплаты */}
                    <div className="package-left">
                        <div className="package-date">
                            Дата покупки:<br/>{formatDate(packageItem.purchased_at)}
                        </div>
                        <div className="package-payment-method">
                            {getPaymentMethodText(packageItem)}
                        </div>
                    </div>

                    {/* Вертикальная черта (только на десктопе) */}
                    <div className="package-divider"></div>

                    {/* Правая часть - информация о пакете */}
                    <div className="package-right">
                        <div className="package-items">
                            <PackageItem item={mainItem} />
                            <div className="package-upgrades">
                                {upgrades.length > 0 ? (
                                    <UpgradeTagsRow>
                                        {upgrades.map((upgradeType) => (
                                            <PurchasedUpgradeTag
                                                key={upgradeType}
                                                upgrade={upgradeType}
                                            />
                                        ))}
                                    </UpgradeTagsRow>
                                ) : (
                                    <div className="package-upgrades__empty">
                                        Без дополнительных проверок
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <ErrorPageView 
                error={error}
                $commitToStore={$commitToStore}
                customAction={{
                    text: 'Попробовать снова',
                    onClick: loadPackages
                }}
            />
        )
    }

    return (
        <div className="packages-list">
            { loading && <ListLoader loading={loading} />}
            {!loading && packages.length === 0 && (
                <div className="empty-state">
                    <div className="empty-state-title">Пакеты не найдены</div>
                    <div className="empty-state-description">
                        У вас нет активных пакетов
                    </div>
                </div>
            )}
            {!loading && packages.length > 0 && (
                <div className="packages-container">
                    {packages.map(renderPackage)}
                </div>
            )}
        </div>
    )
}

export default PackagesList

